import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddSeedAccountDialog from './AddSeedAccountDialog';

/**
 * SeedAccountsPanel — displays the list of seed accounts created inline and
 * provides an "Add Account" button to open AddSeedAccountDialog.
 *
 * The parent controls whether to render this panel (typically when the selected
 * entity/department has zero existing accounts). The panel itself always renders
 * its full content when mounted.
 *
 * Props:
 *   entityId       {string}            — entity ID for the new account
 *   entityName     {string}            — entity name (display + payload)
 *   departmentId   {string}            — department ID for the new account
 *   departmentName {string}            — department name (display + payload)
 *   accounts       {Array}             — seed accounts already created in this session
 *   parentAccounts {Array}             — [{ id, parent_account_name }] from context
 *   onAccountAdded {(account) => void} — called when a new account is successfully created
 */
const SeedAccountsPanel = ({
  entityId,
  entityName,
  departmentId,
  departmentName,
  accounts,
  parentAccounts,
  onAccountAdded,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCreated = (account) => {
    onAccountAdded(account);
    setDialogOpen(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Heading */}
      <Typography variant="subtitle2" gutterBottom>
        Seed Accounts
      </Typography>

      {/* Context line */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {entityId} – {entityName} | {departmentId} – {departmentName}
      </Typography>

      {/* Empty-state guidance */}
      {accounts.length === 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No accounts yet. At least one account is required to proceed to the next step.
        </Alert>
      )}

      {/* List of already-created seed accounts */}
      {accounts.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {accounts.map((acct) => (
            <Chip
              key={acct.account_id ?? acct.id}
              label={`${acct.account_id} – ${acct.account_name}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {/* Add Account button */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
      >
        Add Account
      </Button>

      {/* Dialog */}
      <AddSeedAccountDialog
        open={dialogOpen}
        entityId={entityId}
        entityName={entityName}
        departmentId={departmentId}
        departmentName={departmentName}
        parentAccounts={parentAccounts}
        onClose={() => setDialogOpen(false)}
        onCreated={handleCreated}
      />
    </Box>
  );
};

export default SeedAccountsPanel;
